import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ArrowRight, Handshake, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/common/Loading";
import { SandboxedEmbed } from "@/components/common/SandboxedEmbed";
import { PartnerIcon } from "@/components/partner/partnerIcons";

const scrollToTarget = (target) => {
  if (!target) return;
  const id = String(target).replace(/^#/, "");
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Paragraphs = ({ text, className = "" }) =>
  String(text || "")
    .split(/\n\s*\n/)
    .filter(Boolean)
    .map((p, i) => <p key={i} className={className}>{p}</p>);

const Eyebrow = ({ children, light }) => (
  <span className={`text-xs font-[700] uppercase tracking-[0.18em] ${light ? "text-teal-200" : "text-primary"}`}>{children}</span>
);

export default function Partner() {
  const { data: c, isLoading } = useQuery({
    queryKey: ["partner"],
    queryFn: async () => (await api.get("/partner")).data,
  });

  if (isLoading || !c) return <Loading />;

  return (
    <div data-testid="partner-page">
      {/* ---------------------------------------------------------------- Hero */}
      <section className="relative overflow-hidden border-b border-teal-100 bg-gradient-to-br from-teal-100 via-teal-50 to-background">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-teal-300/30 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 py-24 text-center sm:px-6 lg:py-28">
          <span className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/40">
            <Handshake className="h-8 w-8" />
          </span>
          <h1 className="mt-6 text-4xl font-[760] tracking-[-0.025em] text-foreground sm:text-5xl lg:text-6xl" data-testid="partner-hero-headline">
            {c.hero_headline}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-foreground/70 sm:text-lg">
            {c.hero_subheadline}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" onClick={() => scrollToTarget(c.hero_cta_target)} data-testid="partner-hero-cta">
              {c.hero_cta_label} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="border-primary/40 bg-background/60 text-primary hover:bg-primary/10" onClick={() => scrollToTarget(c.hero_secondary_target)} data-testid="partner-hero-secondary-cta">
              {c.hero_secondary_label}
            </Button>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ Why partner (teal band) */}
      <section data-testid="partner-why" className="relative overflow-hidden border-y border-teal-900/20 bg-gradient-to-br from-[#0f766e] to-[#0a4f4a]">
        <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-teal-400/15 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <Eyebrow light>Why partner</Eyebrow>
              <h2 className="mt-2 text-3xl font-[720] tracking-[-0.02em] text-white sm:text-4xl">{c.why_heading}</h2>
              <div className="mt-5 space-y-4 text-teal-50/90">
                <Paragraphs text={c.why_body} />
              </div>
            </div>
            <ul className="grid grid-cols-1 gap-3 self-center">
              {(c.why_points || []).map((p, i) => (
                <li key={i} className="flex items-start gap-3 rounded-none border border-white/15 bg-white/10 p-4 backdrop-blur transition-colors hover:border-white/35 hover:bg-white/15" data-testid={`partner-why-point-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-300" />
                  <span className="text-sm font-[500] text-white">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ Who (teal band) */}
      <section data-testid="partner-who" className="border-y border-teal-100 bg-teal-50/80">
        <div className="mx-auto max-w-5xl px-4 py-20 text-center sm:px-6 lg:px-8">
          <Eyebrow>Who we partner with</Eyebrow>
          <h2 className="mt-2 text-3xl font-[720] tracking-[-0.02em] text-foreground sm:text-4xl">{c.who_heading}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{c.who_intro}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            {(c.who_types || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-card px-4 py-2 text-sm font-[560] text-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:text-primary-foreground" data-testid={`partner-type-${i}`}>
                <Sparkles className="h-3.5 w-3.5" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ Ways to partner (white) */}
      <section id="ways-to-partner" data-testid="partner-opportunities" className="scroll-mt-20 bg-background">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="text-center">
            <Eyebrow>Partnership opportunities</Eyebrow>
            <h2 className="mt-2 text-3xl font-[720] tracking-[-0.02em] text-foreground sm:text-4xl">{c.opportunities_heading}</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(c.opportunities || []).map((o, i) => (
              <div key={i} className="group relative flex flex-col overflow-hidden rounded-none border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-xl hover:shadow-primary/10" data-testid={`partner-opportunity-${i}`}>
                <span className="absolute inset-x-0 top-0 h-1 bg-primary opacity-0 transition-opacity group-hover:opacity-100" />
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <PartnerIcon name={o.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-[680] text-foreground">{o.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ Benefits (teal gradient band) */}
      <section data-testid="partner-benefits" className="border-y border-teal-100 bg-gradient-to-b from-teal-50 to-teal-100/60">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>What partners receive</Eyebrow>
            <h2 className="mt-2 text-3xl font-[720] tracking-[-0.02em] text-foreground sm:text-4xl">{c.benefits_heading}</h2>
            <p className="mt-4 text-muted-foreground">{c.benefits_intro}</p>
          </div>
          <ul className="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            {(c.benefits || []).map((b, i) => (
              <li key={i} className="flex items-start gap-3 rounded-none bg-card/70 p-3 backdrop-blur" data-testid={`partner-benefit-${i}`}>
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span className="text-sm font-[500] text-foreground">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ------------------------------------------------------------ Why it matters (deep teal) */}
      <section data-testid="partner-why-matters" className="relative overflow-hidden bg-[#04231f]">
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-teal-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-4 py-24 text-center sm:px-6">
          <h2 className="text-3xl font-[720] tracking-[-0.02em] text-white sm:text-4xl">{c.why_matters_heading}</h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-teal-50/85">
            <Paragraphs text={c.why_matters_body} />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ Form (soft teal band) */}
      <section id="partner-form" data-testid="partner-form-section" className="scroll-mt-20 bg-teal-50/40">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <Eyebrow>Get in touch</Eyebrow>
            <h2 className="mt-2 text-3xl font-[720] tracking-[-0.02em] text-foreground sm:text-4xl">{c.form_heading}</h2>
            <p className="mt-4 text-muted-foreground">{c.form_intro}</p>
          </div>
          <div className="mx-auto mt-8 rounded-none border border-teal-100 bg-card p-6 shadow-lg shadow-primary/5 sm:p-8">
            {c.form_embed_html && c.form_embed_html.trim() ? (
              <div data-testid="partner-form-embed">
                <SandboxedEmbed html={c.form_embed_html} title="Partner form" className="partner-embed" minHeight={520} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 rounded-none border-2 border-dashed border-teal-200 bg-teal-50/40 py-16 text-center text-sm text-muted-foreground" data-testid="partner-form-placeholder">
                <Handshake className="h-8 w-8 text-primary/50" />
                <p>Our partnership form will appear here soon.</p>
                <a href="mailto:hello@startupotter.com" className="font-[600] text-primary hover:underline">Or email us at hello@startupotter.com</a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ Partner logos (white) */}
      <section data-testid="partner-logos" className="bg-background">
        <div className="mx-auto max-w-6xl px-4 py-20 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-[720] tracking-[-0.02em] text-foreground sm:text-3xl">{c.partners_heading}</h2>
          {c.partners_coming_soon || !(c.partner_logos || []).length ? (
            <div className="mx-auto mt-5 max-w-md" data-testid="partner-logos-coming-soon">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-[700] uppercase tracking-[0.15em] text-primary-foreground">Coming soon</span>
              <p className="mt-3 text-muted-foreground">{c.partners_coming_soon_text}</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
              {(c.partner_logos || []).map((l, i) => (
                <a key={i} href={l.url || "#"} target={l.url ? "_blank" : undefined} rel="noreferrer" className="flex flex-col items-center gap-2 rounded-none border border-border bg-card p-5 transition-colors hover:border-primary/40" data-testid={`partner-logo-${i}`}>
                  {l.logo_url ? <img src={l.logo_url} alt={l.name} className="h-12 object-contain" /> : <span className="text-base font-[680] text-foreground">{l.name}</span>}
                  {l.label && <span className="text-xs font-[600] uppercase tracking-wide text-primary">{l.label}</span>}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------------------- Final CTA (bold teal) */}
      <section data-testid="partner-final-cta" className="relative overflow-hidden bg-gradient-to-br from-primary to-teal-700">
        <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
          <h2 className="text-3xl font-[760] tracking-[-0.02em] text-white sm:text-4xl">{c.final_heading}</h2>
          <div className="mx-auto mt-5 max-w-2xl space-y-4 text-teal-50/90">
            <Paragraphs text={c.final_body} />
          </div>
          <Button size="lg" variant="secondary" className="mt-8 bg-white text-primary hover:bg-teal-50" onClick={() => scrollToTarget(c.final_cta_target)} data-testid="partner-final-cta-button">
            {c.final_cta_label} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
